// VisaDirectPage.jsx
import React, { useContext, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { HtmlRenderer, Icon } from 'shared-components';
import {
  ArrowLeft,
  CreditCard,
  AlertCircle,
  Shield,
  Clock,
  ChevronDown,
} from 'lucide-react';
import { centsToDollars } from 'shared-components/src/utils/currency';
import { isExemptBin } from 'shared-components/src/utils/binExemption';
import PropTypes from 'prop-types';
import {
  Container,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  CenterHeader,
  BalanceHeader,
  BalanceValue,
  BalanceFee,
  Field,
  Label,
  TextInput,
  Select,
  Help,
  ErrorRow,
  InfoRow,
  AlertRow,
  GridMd2,
  FormHelp,
  SelectWrapper,
  Chevron,
  CollapsibleSection,
} from './styles';
import {
  AlertCard,
  BackButton,
  OutlineButton,
  PrimaryButton,
  ProgressBar,
  ProgressFill,
} from '../style';
import ExpiryDatePicker from '../../../components/ExpiryDatePicker';
import { DisbursementsContext } from '../../../contexts/DisbursementsContext';
import { createExternalAccount, createExternalTransfer } from '../services';
import { formatIntlCurrency } from '../../../utils/helpers';
import { CheckErrorIcon, MoneyPlusIcon } from '../../../components/Icons';

export const createValidators = function createValidators(intl) {
  // Safe message getter with fallbacks
  function t(key, fallback) {
    const messages = intl && intl.messages ? intl.messages : {};
    // prefer singular "visa.errors.*", then fallback to "visa.errors.*"
    let val = messages[key];
    if (typeof val === 'undefined') {
      const alt = key.replace('visa.errors.', 'visa.errors.');
      val = messages[alt];
    }
    return typeof val === 'string' ? val : fallback;
  }

  return {
    fullNameOnCard: function fullNameOnCard(v) {
      const s = (v || '').trim();
      if (!s)
        return t(
          'visa.errors.fullNameOnCard.required',
          'Full name on card is required'
        );
      if (s.length < 2)
        return t(
          'visa.errors.fullNameOnCard.minLength',
          'Name must be at least 2 characters'
        );
      return '';
    },

    address1: function address1(v) {
      const s = (v || '').trim();
      if (!s)
        return t('visa.errors.address1.required', 'Address Line 1 is required');
      if (s.length < 5)
        return t(
          'visa.errors.address1.minLength',
          'Address must be at least 5 characters'
        );
      return '';
    },

    city: function city(v) {
      const s = (v || '').trim();
      if (!s) return t('visa.errors.city.required', 'City is required');
      if (s.length < 2)
        return t(
          'visa.errors.city.minLength',
          'City must be at least 2 characters'
        );
      return '';
    },

    provinceState: function provinceState(v) {
      if (!v)
        return t('visa.errors.provinceState.required', 'Province is required');
      return '';
    },

    postalCode: function postalCode(v) {
      const s = (v || '').trim().toUpperCase();
      if (!s)
        return t('visa.errors.postalCode.required', 'Postal code is required');
      // Canadian postal code format: A1A 1A1 (letter-digit-letter space digit-letter-digit)
      if (!/^[A-Z]\d[A-Z]\s\d[A-Z]\d$/.test(s)) {
        return t(
          'visa.errors.postalCode.invalidFormat',
          'Invalid Canadian postal code format (A1A 1A1)'
        );
      }
      return '';
    },

    country: function country(v) {
      if (!v) return t('visa.errors.country.required', 'Country is required');
      if (v !== 'CA')
        return t('visa.errors.country.onlyCanada', 'Only Canada is supported');
      return '';
    },

    cardNumber: function cardNumber(v, ctx) {
      const clean = (v || '').replace(/\s/g, '');
      if (!clean)
        return t('visa.errors.cardNumber.required', 'Card number is required');
      if (clean.length < 16 || clean.length > 19)
        return t(
          'visa.errors.cardNumber.length',
          'Card number must be 16-19 digits'
        );
      if (!/^\d{16,19}$/.test(clean))
        return t(
          'visa.errors.cardNumber.onlyDigits',
          'Card number must contain only digits'
        );

      const isValidLuhn =
        ctx && typeof ctx.isValidLuhn === 'function' ? ctx.isValidLuhn : null;
      if (!isValidLuhn || !isValidLuhn(clean)) {
        return t('visa.errors.cardNumber.invalid', 'Invalid card number');
      }
      return '';
    },

    expiryMonth: function expiryMonth(v, ctx) {
      if (ctx && ctx.isBinExempt) return '';
      const month = parseInt(v, 10);
      if (!v)
        return t('visa.errors.expiryMonth.required', 'Expiry month is required');
      if (Number.isNaN(month) || month < 1 || month > 12) {
        return t('visa.errors.expiryMonth.invalid', 'Invalid month (1-12)');
      }
      return '';
    },

    expiryYear: function expiryYear(v, ctx) {
      if (ctx && ctx.isBinExempt) return '';
      const currentYear = new Date().getFullYear();
      const year = parseInt(v, 10);
      if (!v)
        return t('visa.errors.expiryYear.required', 'Expiry year is required');
      if (Number.isNaN(year) || year < currentYear)
        return t('visa.errors.expiryYear.expired', 'Card has expired');
      if (year > currentYear + 20)
        return t('visa.errors.expiryYear.invalid', 'Invalid expiry year');

      const hasFormData = ctx && ctx.formData;
      if (hasFormData && ctx.formData.expiryMonth) {
        const currentMonth = new Date().getMonth() + 1;
        const expMonth = parseInt(ctx.formData.expiryMonth, 10);
        if (year === currentYear && expMonth < currentMonth) {
          return t('visa.errors.expiryYear.expired', 'Card has expired');
        }
      }
      return '';
    },

    cvv: function cvv(v, ctx) {
      if (ctx && ctx.isBinExempt) return '';
      if (!v) return t('visa.errors.cvv.required', 'CVV is required');
      if (!/^\d{3}$/.test(v))
        return t('visa.errors.cvv.exactLength', 'CVV must be exactly 3 digits');
      return '';
    },
  };
};

export default function VisaDirectTransfer() {
  const navigate = useNavigate();
  const intl = useIntl();
  const {
    disbursementData = {},
    transferOptions = [],
    redeemToken,
    settransferSuccessData,
  } = useContext(DisbursementsContext);
  const { amount = 0 } = disbursementData || {};

  const [currentError, setCurrentError] = useState(null); // "verification" | "fraud" | "escrow" | null

  const [formData, setFormData] = useState({
    fullNameOnCard: '',
    cardNumber: '',
    expiryMonth: '',
    expiryYear: '',
    cvv: '',
    address1: '',
    address2: '',
    city: '',
    postalCode: '',
    provinceState: '',
    country: 'CA',
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isBinExempt, setIsBinExempt] = useState(false);
  const validators = createValidators(intl);
  /* ========= Handlers ========= */
  const handleInputChange = (field, value) => {
    if (field === 'cardNumber') {
      const cleanValue = value.replace(/\s/g, '').replace(/\D/g, '');
      if (cleanValue.length <= 19) {
        const newIsBinExempt = isExemptBin(cleanValue);
        // Reset CVV and expiry fields when BIN exemption status changes
        if (newIsBinExempt !== isBinExempt) {
          setFormData(prev => ({
            ...prev,
            [field]: cleanValue,
            cvv: '',
            expiryMonth: '',
            expiryYear: '',
          }));
          setErrors(prev => ({
            ...prev,
            cvv: '',
            expiryMonth: '',
            expiryYear: '',
          }));
          setTouched(prev => ({
            ...prev,
            cvv: false,
            expiryMonth: false,
            expiryYear: false,
          }));
        } else {
          setFormData(prev => ({ ...prev, [field]: cleanValue }));
        }
        setIsBinExempt(newIsBinExempt);
      }
    } else if (field === 'postalCode') {
      // Keep the formatted value with space (A1A 1A1 format)
      setFormData(prev => ({ ...prev, [field]: value }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }

    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const isValidLuhn = cardNumber => {
    let sum = 0;
    let isEven = false;
    for (let i = cardNumber.length - 1; i >= 0; i -= 1) {
      let digit = Number.parseInt(cardNumber[i], 10);
      if (isEven) {
        digit *= 2;
        if (digit > 9) digit -= 9;
      }
      sum += digit;
      isEven = !isEven;
    }
    return sum % 10 === 0;
  };

  const validateField = (field, value) => {
    const fn = validators[field];
    let error = '';
    if (typeof fn === 'function') {
      error = fn(value, { formData, isValidLuhn, isBinExempt });
    }
    setErrors(prev => {
      const next = { ...prev };
      next[field] = error;
      return next;
    });
    return error === '';
  };

  const handleBlur = field => {
    setTouched(prev => ({ ...prev, [field]: true }));
    validateField(field, formData[field]);
  };
  const formatCardNumber = value =>
    value
      .replace(/\s/g, '')
      .replace(/(.{4})/g, '$1 ')
      .trim()
      .substring(0, 23);

  const requiredFields = isBinExempt
    ? ['fullNameOnCard', 'address1', 'city', 'provinceState', 'postalCode', 'cardNumber']
    : ['fullNameOnCard', 'address1', 'city', 'provinceState', 'postalCode', 'cardNumber', 'expiryMonth', 'expiryYear', 'cvv'];

  const isFormValid = () =>
    requiredFields.every(f => formData[f] && !errors[f]);

  const validateAllFields = () => {
    let ok = true;
    requiredFields.forEach(f => {
      const v = formData[f];
      const valid = validateField(f, v);
      if (!valid) ok = false;
    });
    setTouched(prev => {
      const t = { ...prev };
      requiredFields.forEach(f => {
        t[f] = true;
      });
      return t;
    });
    return ok;
  };

  const handleSubmit = async () => {
    if (validateAllFields()) {
      setIsLoading(true);
      try {
        // Remove space from postal code for API (A1A1A1 format)
        const postalCodeForApi = formData.postalCode.replace(/\s/g, '');

        const cardPayload = {
          name: formData.fullNameOnCard,
          number: formData.cardNumber,
          address_line1: formData.address1,
          address_line2: formData.address2,
          address_city: formData.city,
          address_state: formData.provinceState,
          address_country: formData.country,
          address_postal_code: postalCodeForApi,
        };

        if (!isBinExempt) {
          cardPayload.cvv = formData.cvv;
          cardPayload.expiry_month = formData.expiryMonth;
          cardPayload.expiry_year = formData.expiryYear;
        }

        const createexternalaccresponse = await createExternalAccount(
          {
            name: formData.fullNameOnCard,
            card: cardPayload,
            type: 'personal_funding_account',
          },
          redeemToken
        );
        const createexternaltransferresponse = await createExternalTransfer(
          {
            transfer_type: 'direct_send_transaction',
            external_account_id: createexternalaccresponse.data.id,
          },
          redeemToken
        );
        settransferSuccessData(createexternaltransferresponse.data);
        navigate('/disbursements/transfer-success');
      } catch (err) {
        const { code } = err.error || {};
        let errorType = 'verification';
        if (code === 'fraud') {
          errorType = 'fraud';
        } else if (code === 'insufficient_escrow_balance' || code === 'escrow_balance_error') {
          errorType = 'insufficient_escrow';
        } else if (code === 'escrow') {
          errorType = 'escrow';
        }
        setCurrentError(errorType);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } finally {
        setIsLoading(false);
      }
    }
  };

  const errorMap = React.useMemo(() => ({
    verification: {
      icon: (
        <AlertCircle size={24} color="#ea580c" style={{ flexShrink: 0 }} />
      ),
      title: intl.messages['visa.errors.verification.title'],
      message: intl.messages['visa.errors.verification.message'],
      variant: 'orange',
    },
    fraud: {
      icon: <Shield size={24} color="#7e22ce" style={{ flexShrink: 0 }} />,
      title: intl.messages['visa.errors.fraud.title'],
      message: intl.messages['visa.errors.fraud.message'],
      variant: 'purple',
    },
    escrow: {
      icon: <Clock size={24} color="#ea580c" style={{ flexShrink: 0 }} />,
      title: intl.messages['visa.errors.escrow.title'],
      message: intl.messages['visa.errors.escrow.message'],
      variant: 'orange',
    },
    insufficient_escrow: {
      icon: <Clock size={24} color="#ea580c" style={{ flexShrink: 0 }} />,
      title: intl.messages['disbursement.error.insufficient_escrow.title'],
      message: intl.messages['disbursement.error.insufficient_escrow.message'],
      variant: 'orange',
    },
  }), [intl]);

  const errorContent = Object.prototype.hasOwnProperty.call(
    errorMap,
    currentError
  )
    ? errorMap[currentError]
    : null;

  function formatExpiry(m, y) {
    if (!m || !y) return '';
    const mm = String(m).padStart(2, '0');
    const yy = String(y).slice(-2);
    return `${mm}-${yy}`;
  }

  const expiryDateValue = formatExpiry(
    formData.expiryMonth,
    formData.expiryYear
  );

  const expiryDateError = () => (
    (touched.expiryMonth || touched.expiryYear) &&
      (errors.expiryMonth || errors.expiryYear) && (
      <ErrorRow>
        <AlertCircle size={16} />
        <span>{errors.expiryMonth || errors.expiryYear}</span>
      </ErrorRow>
    )
  );
  /* ========= Render ========= */
  return (
    <>
      {/* Progress */}
      <ProgressBar>
        <ProgressFill className="seventy" />
      </ProgressBar>

      <Container>
        {/* Back */}
        <BackButton onClick={() => window.history.back()}>
          <ArrowLeft size={16} />
          {intl.messages['common.goBack']}
        </BackButton>

        {/* Error Banner */}
        {errorContent && (
          <AlertCard $variant={errorContent.variant}>
            <AlertRow>
              <CheckErrorIcon
                width={24}
                height={24}
                fill="#D60000"
                style={{ flexShrink: 0 }}
              />
              <div>
                <h3>{errorContent.title}</h3>
                <p><HtmlRenderer html={errorContent.message} /></p>
                <OutlineButton onClick={() => setCurrentError(null)}>
                  {intl.messages['visa.errors.dismiss']}
                </OutlineButton>
              </div>
            </AlertRow>
          </AlertCard>
        )}

        {/* Heading */}
        <CenterHeader>
          <h1>{intl.messages['visa.title']}</h1>
          <p>
            {intl.messages['visa.subtitle']}{' '}
            <b>{intl.messages['visa.subtitle.visa']}</b>{' '}
            {intl.messages['visa.subtitle._']}
          </p>
        </CenterHeader>

        {/* Balance Card */}
        <Card className="green-dark">
          <CardBody style={{ textAlign: 'center' }}>
            <BalanceHeader>
              <MoneyPlusIcon size={40} />
              <h2>{intl.messages['visa.balance.title']}</h2>
            </BalanceHeader>
            <div>
              <BalanceValue>
                {formatIntlCurrency(centsToDollars(amount || 0), intl.locale)}
              </BalanceValue>
              <BalanceFee>
                {intl.messages['visa.balance.fee']}{' '}
                {formatIntlCurrency(
                  transferOptions.find(opt => opt.type === 'visa_direct').fee ||
                    0,
                  intl.locale
                )}
              </BalanceFee>
            </div>
          </CardBody>
        </Card>

        {/* Card Details Form */}
        <Card style={{ border: 0 }}>
          <CardHeader>
            <CardTitle>
              <CreditCard size={32} color="#231F20" />
              {intl.messages['visa.card.title']}
            </CardTitle>
          </CardHeader>
          <CardBody style={{ display: 'grid', gap: '1.5rem' }}>
            {/* Full Name on Card */}
            <Field>
              <Label htmlFor="fullNameOnCard">
                {intl.messages['visa.nameOnCard']}
              </Label>
              <TextInput
                id="fullNameOnCard"
                type="text"
                placeholder={intl.messages['visa.nameOnCard.placeholder']}
                value={formData.fullNameOnCard}
                onChange={e => {
                  handleInputChange('fullNameOnCard', e.target.value);
                }}
                onBlur={() => handleBlur('fullNameOnCard')}
                $error={!!errors.fullNameOnCard}
              />
              {touched.fullNameOnCard && errors.fullNameOnCard && (
                <ErrorRow>
                  <AlertCircle size={16} />
                  <span>{errors.fullNameOnCard}</span>
                </ErrorRow>
              )}
              <Help>{intl.messages['visa.nameOnCard.help']}</Help>
            </Field>

            {/* Card Number */}
            <Field>
              <Label htmlFor="cardNumber">
                {intl.messages['visa.cardNumber']}
              </Label>
              <TextInput
                id="cardNumber"
                type="text"
                value={formatCardNumber(formData.cardNumber)}
                onChange={e => handleInputChange('cardNumber', e.target.value)}
                onBlur={() => handleBlur('cardNumber')}
                placeholder="1234 5678 9012 3456"
                maxLength={23}
                $error={!!errors.cardNumber}
              />
              {touched.cardNumber && errors.cardNumber && (
                <ErrorRow>
                  <AlertCircle size={16} />
                  <span>{errors.cardNumber}</span>
                </ErrorRow>
              )}
            </Field>

            <div>
              {/* CVV + Expiry - hidden by default, show only when card number >= 6 digits and BIN is not exempt */}
              <CollapsibleSection $visible={formData.cardNumber.length >= 6 && !isBinExempt}>
                <GridMd2>
                  <Field>
                    <Label htmlFor="cvv">{intl.messages['visa.cvv']} *</Label>
                    <TextInput
                      id="cvv"
                      type="text"
                      placeholder="123"
                      maxLength={3}
                      value={formData.cvv}
                      onChange={e => {
                        const v = e.target.value.replace(/\D/g, '');
                        if (v.length <= 3) handleInputChange('cvv', v);
                      }}
                      onBlur={() => handleBlur('cvv')}
                      $error={!!errors.cvv}
                    />
                    {touched.cvv && errors.cvv && (
                      <ErrorRow>
                        <AlertCircle size={16} />
                        <span>{errors.cvv}</span>
                      </ErrorRow>
                    )}
                  </Field>

                  <Field>
                    <Label htmlFor="expiry">{intl.messages['visa.expiry']} *</Label>
                    <div>
                      <ExpiryDatePicker
                        value={expiryDateValue}
                        onChange={(month, year) => {
                          handleInputChange('expiryMonth', month);
                          handleInputChange('expiryYear', year);
                        }}
                        onBlur={() => {
                          handleBlur('expiryMonth');
                          handleBlur('expiryYear');
                        }}
                        error={!!(errors.expiryMonth || errors.expiryYear)}
                      />
                    </div>
                    {expiryDateError()}
                  </Field>
                </GridMd2>
              </CollapsibleSection>

              {/* Info message when CVV/Expiry not needed */}
              <InfoRow $visible={formData.cardNumber.length > 0 && formData.cardNumber.length >= 6 && isBinExempt}>
                <Icon type="info" size={16} />
                <span>{intl.messages['visa.noCvvNeeded'] || 'No CVV needed.'}</span>
              </InfoRow>
            </div>
            {/* Address 1 + Address 2 */}
            <GridMd2>
              <Field>
                <Label htmlFor="address1">
                  {intl.messages['visa.address1']}
                </Label>
                <TextInput
                  id="address1"
                  type="text"
                  value={formData.address1}
                  onChange={e => handleInputChange('address1', e.target.value)}
                  onBlur={() => handleBlur('address1')}
                  $error={!!errors.address1}
                />
                {touched.address1 && errors.address1 && (
                  <ErrorRow>
                    <AlertCircle size={16} />
                    <span>{errors.address1}</span>
                  </ErrorRow>
                )}
                <Help>{intl.messages['visa.address1.help']}</Help>
              </Field>

              <Field>
                <Label htmlFor="address2">
                  {intl.messages['visa.address2']}
                </Label>
                <TextInput
                  id="address2"
                  type="text"
                  value={formData.address2}
                  onChange={e => handleInputChange('address2', e.target.value)}
                  onBlur={() => handleBlur('address2')}
                  $error={!!errors.address2}
                />
                {touched.address2 && errors.address2 && (
                  <ErrorRow>
                    <AlertCircle size={16} />
                    <span>{errors.address2}</span>
                  </ErrorRow>
                )}
              </Field>
            </GridMd2>

            {/* City + Province */}
            <GridMd2>
              <Field>
                <Label htmlFor="city">{intl.messages['visa.city']}</Label>
                <TextInput
                  id="city"
                  type="text"
                  value={formData.city}
                  onChange={e => handleInputChange('city', e.target.value)}
                  onBlur={() => handleBlur('city')}
                  $error={!!errors.city}
                />
                {touched.city && errors.city && (
                  <ErrorRow>
                    <AlertCircle size={16} />
                    <span>{errors.city}</span>
                  </ErrorRow>
                )}
              </Field>

              <Field>
                <Label htmlFor="provinceState">
                  {intl.messages['visa.provinceState']}
                </Label>
                <ProvinceSelect
                  id="provinceState"
                  value={formData.provinceState}
                  onChange={e => {
                    handleInputChange('provinceState', e.target.value);
                  }}
                  onBlur={() => handleBlur('provinceState')}
                  $error={!!errors.provinceState}
                />
                {touched.provinceState && errors.provinceState && (
                  <ErrorRow>
                    <AlertCircle size={16} />
                    <span>{errors.provinceState}</span>
                  </ErrorRow>
                )}
              </Field>
            </GridMd2>

            {/* Postal + Country */}
            <GridMd2>
              <Field>
                <Label htmlFor="postalCode">
                  {intl.messages['visa.postalCode']}
                </Label>
                <TextInput
                  id="postalCode"
                  type="text"
                  placeholder="A1A 1A1"
                  value={formData.postalCode}
                  onChange={e => {
                    // Handle Canadian postal code mask: A1A 1A1 (letter-digit-letter space digit-letter-digit)
                    const input = e.target.value.toUpperCase();
                    let result = '';
                    let charIndex = 0;

                    // Pattern: L D L space D L D (L=letter, D=digit)
                    const pattern = ['L', 'D', 'L', ' ', 'D', 'L', 'D'];

                    for (let i = 0; i < input.length && charIndex < pattern.length; i += 1) {
                      const char = input[i];
                      const expected = pattern[charIndex];

                      if (expected === ' ') {
                        result += ' ';
                        charIndex += 1;
                        // Re-check current char against next pattern position
                        i -= 1;
                      } else if (expected === 'L' && /[A-Z]/.test(char)) {
                        result += char;
                        charIndex += 1;
                      } else if (expected === 'D' && /[0-9]/.test(char)) {
                        result += char;
                        charIndex += 1;
                      }
                      // Skip invalid characters
                    }

                    handleInputChange('postalCode', result);
                  }}
                  onBlur={() => handleBlur('postalCode')}
                  $error={!!errors.postalCode}
                  maxLength={7}
                />
                {touched.postalCode && errors.postalCode && (
                  <ErrorRow>
                    <AlertCircle size={16} />
                    <span>{errors.postalCode}</span>
                  </ErrorRow>
                )}
              </Field>

              <Field>
                <Label htmlFor="country">{intl.messages['visa.country']}</Label>
                <TextInput
                  id="country"
                  type="text"
                  value="Canada"
                  readOnly
                  disabled
                />
              </Field>
            </GridMd2>
          </CardBody>
        </Card>

        {/* Submit */}
        <div
          style={{
            textAlign: 'center',
            padding: '0 1rem',
            marginBottom: '2rem',
          }}
        >
          <PrimaryButton
            onClick={handleSubmit}
            disabled={!isFormValid() || isLoading}
          >
            {intl.messages['visa.submitTransfer']}
          </PrimaryButton>
          {!isFormValid() && (
            <FormHelp style={{ marginTop: '.75rem' }}>
              {intl.messages['visa.button.help']}
            </FormHelp>
          )}
        </div>
      </Container>
    </>
  );
}

const ProvinceSelect = props => {
  const intl = useIntl();
  const { onChange, onBlur, value = '' } = props;
  const [open, setOpen] = useState(false);
  const selectRef = React.useRef(null);

  const handleFocus = () => {
    setOpen(true);
  };

  const handleBlur = e => {
    // Use setTimeout to allow the change event to fire first
    setTimeout(() => {
      setOpen(false);
      if (onBlur) {
        onBlur(e);
      }
    }, 0);
  };

  const handleChange = e => {
    if (onChange) {
      onChange(e);
    }
    setOpen(false);
  };

  return (
    <SelectWrapper>
      <Select
        {...props}
        ref={selectRef}
        value={value}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onChange={handleChange}
      >
        <option value="">
          {intl.messages['visa.provinceState.placeholder']}
        </option>
        <option value="AB">Alberta</option>
        <option value="BC">British Columbia</option>
        <option value="MB">Manitoba</option>
        <option value="NB">New Brunswick</option>
        <option value="NL">Newfoundland and Labrador</option>
        <option value="NS">Nova Scotia</option>
        <option value="ON">Ontario</option>
        <option value="PE">Prince Edward Island</option>
        <option value="QC">Quebec</option>
        <option value="SK">Saskatchewan</option>
        <option value="NT">Northwest Territories</option>
        <option value="NU">Nunavut</option>
        <option value="YT">Yukon</option>
      </Select>
      <Chevron $open={open}>
        <ChevronDown size={16} color="#231f20" />
      </Chevron>
    </SelectWrapper>
  );
};

ProvinceSelect.propTypes = {
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func.isRequired,
  value: PropTypes.string,
};
